import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useRTL } from '../../../hooks/useRTL';
import {
    Card,
    CardContent,
    CardActions,
    Typography,
    Button,
    Box,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Paper
} from '@mui/material';
import {
    Person as PersonIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Assessment as StatsIcon
} from '@mui/icons-material';
import { useAuth } from '../../../context/AuthContext';

const RadiologistCard = ({ radiologist, onEdit, onDelete, canEdit, canDelete }) => {
    const { t } = useTranslation();
    const { isRTL } = useRTL();
    const navigate = useNavigate();
    const { isSuperAdmin } = useAuth()

    const handleViewStats = () => {
        navigate(`/radiologists/${radiologist._id}/statistics`);
    };

    return (
        <Card>
            <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                    <PersonIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
                    <Box>
                        <Typography variant="h6" component="h2">
                            {radiologist?.name || 'N/A'}
                        </Typography>
                        <Chip
                            label={radiologist?.isActive ? t('common.active') : t('common.inactive')}
                            color={radiologist?.isActive ? 'success' : 'default'}
                            size="small"
                        />
                    </Box>
                </Box>

                <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }} dir={isRTL ? 'rtl' : 'ltr'}>
                    <Table size="small">
                        <TableBody>
                            {radiologist?.gender && (
                                <TableRow>
                                    <TableCell component="th" sx={{ fontWeight: 'bold', width: '40%' }} align={isRTL ? 'right' : 'left'}>
                                        {t('radiologists.gender')}
                                    </TableCell>
                                    <TableCell align={isRTL ? 'left' : 'right'}>
                                        {t(`genders.${radiologist.gender}`)}
                                    </TableCell>
                                </TableRow>
                            )}
                            {radiologist?.age && (
                                <TableRow>
                                    <TableCell component="th" sx={{ fontWeight: 'bold' }} align={isRTL ? 'right' : 'left'}>
                                        {t('radiologists.age')}
                                    </TableCell>
                                    <TableCell align={isRTL ? 'left' : 'right'}>
                                        {radiologist.age} {t('patients.years')}
                                    </TableCell>
                                </TableRow>
                            )}
                            {radiologist?.phoneNumber && (
                                <TableRow>
                                    <TableCell component="th" sx={{ fontWeight: 'bold' }} align={isRTL ? 'right' : 'left'}>
                                        {t('radiologists.phoneNumber')}
                                    </TableCell>
                                    <TableCell align={isRTL ? 'left' : 'right'}>
                                        {radiologist.phoneNumber}
                                    </TableCell>
                                </TableRow>
                            )}
                            {radiologist?.totalScansPerformed !== undefined && (
                                <TableRow>
                                    <TableCell component="th" sx={{ fontWeight: 'bold' }} align={isRTL ? 'right' : 'left'}>
                                        {t('radiologists.totalScans')}
                                    </TableCell>
                                    <TableCell align={isRTL ? 'left' : 'right'}>
                                        {radiologist.totalScansPerformed}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </CardContent>
            <CardActions sx={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                {isSuperAdmin &&
                    <Button
                        size="small"
                        startIcon={<StatsIcon />}
                        onClick={handleViewStats}
                        color="info"
                    >
                        {t('radiologists.viewStats', 'View Stats')}
                    </Button>
                }

                <Box>
                    {canEdit && (
                        <Button size="small" startIcon={<EditIcon />} onClick={onEdit}>
                            {t('common.edit')}
                        </Button>
                    )}
                    {canDelete && (
                        <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={onDelete}>
                            {t('common.delete')}
                        </Button>
                    )}
                </Box>
            </CardActions>
        </Card>
    );
};

export default RadiologistCard;